import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useParams } from "react-router"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import {
  deleteInterviewAppointmentCandidate,
  getInterviewAppointmentCandidates,
} from "store/actions/interviewsAction"
import NoDataAvailable from "components/Shared/NoDataAvailable"
import { API_URL, handleStopPropagation } from "components/Common/Utils"
import DeleteModal from "components/Common/DeleteModal"
import { Filter, FilterX, Plus, Trash } from "lucide-react"
import { showAlert } from "store/reducers/alertReducer"
import Filters from "components/Shared/Filters"
import Paginations from "components/Common/Pagination"
import Spinners from "components/Shared/Spinners"
import { usePermissions } from "hooks"
import { useSearchParams } from "react-router-dom"
import AddCandidateInPool from "../AddCandidateInPool"
import CandidateScheduledForInterview from "./CandidateScheduledForInterview"
import axios from "axios"
import { getJobTitlesList } from "store/actions/commonAction"

const Candidates = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  document.title = t("Interview Appointment Candidates | Yamama")
  const {
    loading,
    interviewAppointmentCandidates,
    perPage,
    totalPages,
    totalCount,
    currentPage,
    isLoading,
  } = useSelector(state => state.Interviews)
  const { jobTitlesList } = useSelector(state => state.Common)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { uuid } = useParams()
  const { search } = useLocation()
  const { showAll, permissions } = usePermissions("interview_schedule")

  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedUuid, setSelectedUuid] = useState(null)
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1
  const [activeTab, setActiveTab] = useState(-1)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [isExporting, setIsExporting] = useState(false)

  const [filtersState, setFiltersState] = useState(() => {
    const searchKeyword = searchParams.get("searchKeyword")
    const is_trial_run = searchParams.get("is_trial_run")
    const job_title_ids = searchParams.get("job_title_ids")

    return {
      searchKeyword: searchKeyword || "",
      is_trial_run: is_trial_run,
      job_title_ids: job_title_ids || [],
    }
  })
  const [isShowFilter, setIsShowFilter] = useState(() => {
    const showFilterParam = searchParams.get("showFilter")
    return showFilterParam === "1" // '1' means true, anything else is false
  })
  useEffect(() => {
    if (
      activeTab !== "SCHEDULE_FOR_INTERVIEW" &&
      activeTab !== "INTERVIEW_COMPLETED"
    ) {
      getAllData()
    }
  }, [activeTab])

  useEffect(() => {
    dispatch(getJobTitlesList())
  }, [])

  const getAllData = (page, keyword = "", isReset = false) => {
    const { searchKeyword } = filtersState

    const payload = {
      schedule_uuid: uuid,
      limit: perPage,
      page_number: page || pageNumber,
      order_by: 1,
      filters: {
        type:
          typeof activeTab !== "string"
            ? activeTab
            : activeTab === "SCHEDULE_FOR_INTERVIEW"
            ? 1
            : 2, // -1 All,0: Invited/Pending,1:Accepted or 2:Rejected or Not Accepted
        keyword: isReset ? "" : searchKeyword,
        is_trial_run:
          activeTab === "INTERVIEW_COMPLETED"
            ? filtersState.is_trial_run === "no"
              ? 0
              : filtersState.is_trial_run === "yes"
              ? 1
              : null
            : null,
        job_title_ids:
          activeTab === "INTERVIEW_COMPLETED" ? filtersState.job_title_ids : [],
      },
    }

    dispatch(getInterviewAppointmentCandidates(payload))
    if (isReset) {
      // If resetting filters, only keep page and tab in URL
      navigate(
        `/interview-appointments/${uuid}/candidates?page=${
          page || pageNumber
        }&tab=${activeTab}`
      )
    } else {
      navigateWithFilters(page)
    }
  }

  const navigateWithFilters = (page, tab) => {
    const params = new URLSearchParams()

    // Always add page and tab
    params.set("page", page || pageNumber)
    params.set("tab", tab !== undefined ? tab : activeTab)
    params.set("showFilter", isShowFilter ? "1" : "0")

    // Only add filters that have been set
    if (filtersState.searchKeyword)
      params.set("searchKeyword", filtersState.searchKeyword)
    if (filtersState.is_trial_run !== null)
      params.set("is_trial_run", filtersState.is_trial_run)
    if (filters?.job_title_ids?.length > 0)
      params.set("job_title_ids", filters.job_title_ids)
    navigate(`/interview-appointments/${uuid}/candidates?${params.toString()}`)
  }

  const onPageChange = page => {
    getAllData(page)
  }

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab)
    }
  }

  const handleSearch = () => {
    if (
      activeTab == "SCHEDULE_FOR_INTERVIEW" ||
      activeTab == "INTERVIEW_COMPLETED"
    ) {
      getAllDataFromChild(1)
    } else {
      getAllData(1)
    }
  }

  const handleReset = () => {
    setFiltersState({
      searchKeyword: "",
      is_trial_run: "no",
      job_title_ids: [],
    })
    if (
      activeTab == "SCHEDULE_FOR_INTERVIEW" ||
      activeTab == "INTERVIEW_COMPLETED"
    ) {
      getAllDataFromChild(1, true)
    } else {
      getAllData(1, "", true) // Reset search and go to page 1
    }
  }

  const handleDelete = () => {
    dispatch(deleteInterviewAppointmentCandidate(itemIdToDelete))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          getAllData()
          setShowDeleteModal(false)
          setItemIdToDelete(null)
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const formattedJobTitles = useMemo(() => {
    if (!jobTitlesList) return []

    return jobTitlesList.map(jobTitle => ({
      value: jobTitle.id,
      label: jobTitle[language === "en" ? "name" : "name_ar"],
    }))
  }, [jobTitlesList])

  const filters = {
    searchKeyword: {
      type: "text",
      placeholder: t("Search by name,national id or phone number"),
      label: t("Search by name,national id or phone number"),
      colSize: activeTab === "INTERVIEW_COMPLETED" ? 4 : 6,
    },
  }

  if (activeTab === "INTERVIEW_COMPLETED") {
    filters.is_trial_run = {
      type: "select",
      placeholder: t("Trial Operation"),
      label: t("Select Trial Operation"),
      options: [
        { value: "all", label: t("All") },
        { value: "no", label: t("No") },
        { value: "yes", label: t("Yes") },
      ],
      colSize: 4,
    }
    filters.job_title_ids = {
      type: "select",
      placeholder: t("Select Job Title"),
      label: t("Job Title"),
      colSize: 4,
      options: formattedJobTitles,
      multiple: true,
    }
  }

  const toggleFilterVisibility = () => {
    const newState = !isShowFilter
    setIsShowFilter(newState)

    // Update URL with new visibility state
    const newParams = new URLSearchParams(searchParams)
    newParams.set("showFilter", newState ? "1" : "0")
    setSearchParams(newParams)
  }

  let getAllDataFromChild = () => {}

  const handleExport = async () => {
    try {
      setIsExporting(true)
      const authUserString = localStorage.getItem("authUser")
      const authUser = authUserString ? JSON.parse(authUserString) : null
      const token = authUser?.token

      let data = {}

      if (typeof activeTab !== "string") {
        data = {
          export_type: "interview_invitation_lists",
          filters: {
            interview_schedule_uuid: uuid, // when we need to export specific schedule id records
            keyword: filtersState.searchKeyword,
            type: activeTab,
          },
        }
      } else {
        data = {
          export_type: "interview_lists",
          filters: {
            interview_schedule_uuid: uuid, // when we need to export specific schedule id records
            keyword: filtersState.searchKeyword,
            type: activeTab === "SCHEDULE_FOR_INTERVIEW" ? 1 : 2, //(by default -1),-1:All,0:Invited,1:Pending,2:Compelte ,3:Cancelled
            is_trial_run:
              activeTab === "SCHEDULE_FOR_INTERVIEW"
                ? null
                : filtersState.is_trial_run === "all"
                ? null
                : filtersState.is_trial_run === "no"
                ? 0
                : 1,
            job_title_ids:
              activeTab === "SCHEDULE_FOR_INTERVIEW"
                ? []
                : filtersState.job_title_ids,
          },
        }
      }

      const response = await axios.post(
        `${API_URL}/api/v1/admin/export/by-type`,
        data,
        {
          headers: {
            Authorization: token, // Attach token in headers
          },
          responseType: "blob", // Important for file downloads
        }
      )

      const contentType = response.headers["content-type"]

      if (contentType === "application/json") {
        return dispatch(
          showAlert({
            text: t("Export initiated, you will be notified once done."),
            type: "success",
          })
        )
      }

      // Create a URL for the downloaded file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        activeTab === -1
          ? "All Interview Appointment Candidates.xlsx"
          : activeTab === 0
          ? "Pending Acceptance.xlsx"
          : activeTab === 1
          ? "Accepted.xlsx"
          : activeTab === 3
          ? "Cancelled.xlsx"
          : activeTab === "SCHEDULE_FOR_INTERVIEW"
          ? "Scheduled For Interview.xlsx"
          : "Interview Completed.xlsx"
      ) // Set filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url) // Clean up memory
    } catch (error) {
      console.error("Export failed:", error.response?.data || error.message)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        {/* {loading ? (
          <Spinners />
        ) : ( */}
        <Container fluid>
          <Row>
            <Col xl="12">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="g_between">
                  <i
                    className="bx bx-arrow-back mb-3 fs-5 cursor-pointer"
                    onClick={() => navigate(`/interview-appointments`)}
                  ></i>
                  <h4 className="card-title ">
                    {t("Interview Appointment Candidates")}
                  </h4>
                </div>
                <div className="d-flex gap-3">
                  {(showAll ||
                    permissions.includes("enable_schedule_export")) && (
                    <button
                      className="main_btn fit_content"
                      onClick={handleExport}
                      disabled={isExporting}
                    >
                      {t("Download XLSX")}
                    </button>
                  )}
                  <Button
                    color="success"
                    onClick={() => {
                      toggle()
                      setSelectedUuid(uuid)
                    }}
                    title={t("Add Candidates")}
                  >
                    <Plus />
                  </Button>
                  <Button
                    color="primary"
                    onClick={toggleFilterVisibility}
                    title={t("Show/Hide Filter")}
                  >
                    {isShowFilter ? <FilterX /> : <Filter />}
                  </Button>
                </div>
              </div>

              <Nav tabs className="one_line_parent mb-2">
                <NavItem>
                  <NavLink
                    className={activeTab == -1 ? "active" : ""}
                    onClick={() => toggleTab(-1)}
                  >
                    {t("All")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab == 0 ? "active" : ""}
                    onClick={() => toggleTab(0)}
                  >
                    {t("Pending Acceptance")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab == 1 ? "active" : ""}
                    onClick={() => toggleTab(1)}
                  >
                    {t("Accepted")}
                  </NavLink>
                </NavItem>
                {/* <NavItem>
                  <NavLink
                    className={activeTab == 2 ? "active" : ""}
                    onClick={() => toggleTab(2)}
                  >
                    {t("Completed")}
                  </NavLink>
                </NavItem> */}
                <NavItem>
                  <NavLink
                    className={activeTab == 3 ? "active" : ""}
                    onClick={() => toggleTab(3)}
                  >
                    {t("Cancelled")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={
                      activeTab == "SCHEDULE_FOR_INTERVIEW" ? "active" : ""
                    }
                    onClick={() => toggleTab("SCHEDULE_FOR_INTERVIEW")}
                  >
                    {t("Scheduled for Interview")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={
                      activeTab == "INTERVIEW_COMPLETED" ? "active" : ""
                    }
                    onClick={() => toggleTab("INTERVIEW_COMPLETED")}
                  >
                    {t("Interview Completed")}
                  </NavLink>
                </NavItem>
              </Nav>
              {isShowFilter && (
                <Filters
                  filters={filters}
                  state={filtersState}
                  setState={setFiltersState}
                  handleSearch={handleSearch}
                  handleReset={handleReset}
                  t={key => key} // Replace with actual translation function
                />
              )}
              {activeTab == "SCHEDULE_FOR_INTERVIEW" ||
              activeTab == "INTERVIEW_COMPLETED" ? (
                <CandidateScheduledForInterview
                  setGetAllData={fn => (getAllDataFromChild = fn)}
                  filtersState={filtersState}
                  activeTab={activeTab}
                />
              ) : loading ? (
                <Spinners />
              ) : (
                <>
                  <Card className="user_personal_info mt-3 theme_card">
                    <CardBody>
                      <div className="table-responsive">
                        {!loading &&
                        interviewAppointmentCandidates?.length === 0 ? (
                          <NoDataAvailable />
                        ) : (
                          <Table className="table mb-0">
                            <thead className="table-light full_th">
                              <tr>
                                <th>{t("S. No.")}</th>
                                <th>{t("Name")}</th>
                                <th>{t("Phone")}</th>
                                <th>{t("National Id")}</th>
                                <th>{t("Created At")}</th>
                                <th>{t("Status")}</th>
                                <th>
                                  {!(activeTab === 2 || activeTab === 3) &&
                                    t("Action")}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {interviewAppointmentCandidates?.map(
                                (item, index) => (
                                  <tr key={index}>
                                    <th>
                                      {(currentPage - 1) * perPage + index + 1}
                                    </th>
                                    <td>{item?.user?.name}</td>
                                    <td>
                                      <p className="ltr fit_content">
                                        {item?.user?.phone}
                                      </p>
                                    </td>
                                    <td>{item?.user?.national_id}</td>
                                    <td>
                                      <p className="ltr fit_content">
                                        {item?.created_at}
                                      </p>
                                    </td>
                                    <td>
                                      <p
                                        className={`${
                                          item?.status === 0
                                            ? "text-secondary"
                                            : item?.status === 1
                                            ? "text-primary"
                                            : item?.status === 2
                                            ? "text-success"
                                            : item?.status === 3
                                            ? "text-danger"
                                            : ""
                                        }`}
                                      >
                                        {item?.status === 0
                                          ? t("Invited")
                                          : item?.status === 1
                                          ? t("Accepted")
                                          : item?.status === 2
                                          ? t("Completed")
                                          : item?.status === 3
                                          ? t("Cancelled")
                                          : ""}
                                      </p>
                                    </td>
                                    <td onClick={handleStopPropagation}>
                                      {/* {!(activeTab === 2 || activeTab === 3) && ( */}
                                      {!(
                                        item?.status === 2 || item?.status === 3
                                      ) && (
                                        <div className="d-flex gap-2 action_buttons">
                                          {(showAll ||
                                            permissions.includes(
                                              "can_delete_invitation"
                                            )) && (
                                            <Button
                                              color="danger"
                                              onClick={() => {
                                                setItemIdToDelete(item.id)
                                                setShowDeleteModal(true)
                                              }}
                                              disabled={
                                                isLoading &&
                                                item?.uuid === itemIdToDelete
                                              }
                                              title={t("Remove")}
                                            >
                                              <Trash />
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </Table>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                  {!loading && interviewAppointmentCandidates.length > 0 && (
                    <Paginations
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      onPageChange={onPageChange}
                    />
                  )}
                </>
              )}
            </Col>
          </Row>
        </Container>
        {/* )} */}
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        isLoading={isLoading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Candidate"),
        })}
      />
      <Modal
        isOpen={modal}
        toggle={toggle}
        size="lg"
        className=""
        backdrop="static"
      >
        <ModalHeader toggle={toggle}>
          {t("Add Candidates In The Interview")}
        </ModalHeader>
        <ModalBody>
          <AddCandidateInPool
            toggle={toggle}
            selectedUuid={selectedUuid}
            getAllData={getAllData}
          />
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default Candidates
